import {SecurityMapOf} from "../map/SecurityMap";
import {BehaviorSubject, Observable, ReplaySubject, Subject} from "rxjs";
import {CodeAndExchange, Securities} from "../../../instruments/types/CodeAndExchange";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";
import {SecurityInformationService} from "../../info/service/SecurityInformationService";
import {ClientEventService} from "../../../services/client-events/ClientEventService";
import {SystemEventService} from "../../../services/system-state/SystemEventService";
import {ClientEventType} from "../../../services/client-events/models/ClientEventType";
import {SystemEvent} from "../../../services/system-state/SystemEvent";
import {IsoDate} from "../../../pseudo-types/DateTypes";
import {PartialObserver} from "rxjs/Observer";
import {ArrayUtil} from "../../../js/util/ArrayUtil";

export abstract class SecurityDataStore<T extends CodeAndExchange> {

    private readonly sources = SecurityMapOf<Subject<T>>();

    private readonly requiresRequest$ = new ReplaySubject<CodeAndExchange>(1);
    private readonly lastReload$ = new BehaviorSubject(DateTimeUtil.isoToday());

    protected constructor(private readonly clientEventService: ClientEventService,
                          private readonly systemEventService: SystemEventService) {
        this.init();
    }

    private init() {
        this.bufferRequests();
        this.watchInfoUpdates();
        this.watchIdleEnd();
    }

    private bufferRequests() {
        const buffer$ = this.requiresRequest$.throttleTime(100).delay(100);
        this.requiresRequest$
            .buffer(buffer$)
            .filter((buffer) => buffer.length > 0)
            .switchMap((securities) => this.retrieveData(securities))
            .subscribe((records) => this.updateData(records));
    }

    protected abstract retrieveData(securities: Securities): Observable<ReadonlyArray<T>>;

    private updateData(records: ReadonlyArray<T>) {
        records.forEach((record) => {
            const subject = this.sources.computeIfAbsent(record, (_) => new ReplaySubject<T>(1));
            subject.next(record);
        });
    }

    private watchInfoUpdates() {
        this.clientEventService
            .events()
            .filter((evt) => evt.type === ClientEventType.SECURITY_INFO_UPDATED)
            .subscribe(() => this.reloadAll());
    }

    /* When we end idle we may have missed an update event due to overnight, so we reload manually if so */
    private watchIdleEnd() {
        this.systemEventService
            .idle()
            .filter((evt) => evt === SystemEvent.IDLE_END)
            .withLatestFrom(this.lastReload$)
            .filter(([_, lastReload]) => this.isNewDay(lastReload))
            .subscribe(() => this.reloadAll());
    }

    private isNewDay(lastReload: IsoDate): boolean {
        const now = DateTimeUtil.isoToday();
        return now > lastReload;
    }

    private reloadAll() {
        const observer: PartialObserver<ReadonlyArray<T>> = {
            next: (records) => this.updateData(records),
            complete: () => this.lastReload$.next(DateTimeUtil.isoToday())
        };

        Observable.of(this.sources)
                  .map((s) => s.keys())
                  .flatMap((keys) => this.retrieveData(keys))
                  .subscribe(observer);
    }

    public stream(security: CodeAndExchange | Securities): Observable<T> {
        if (ArrayUtil.isArray(security)) {
            const sources = security.map((s) => this.stream(s));
            return Observable.merge(...sources);
        } else {
            return this.source(security);
        }
    }

    private source(security: CodeAndExchange): Observable<T> {
        if (this.sources.has(security)) {
            return this.sources.get(security)!;
        } else {
            const source = new ReplaySubject<T>(1);
            this.sources.set(security, source);
            this.requiresRequest$.next(security);
            return source;
        }
    }
}
